/* Shared shape for the privacy notice and the terms (English, Arabic, and Hindi and Indonesian translated from the English).
   Inline marks inside strings: [placeholder] = a company fact not decided yet (shown highlighted);
   {label|/path} = a link to one of our pages (or an https URL). */
import type { Lang } from '@pathabroad/core/browser';

export type Block =
  | { p: string }
  | { ul: string[] }
  | { dl: Array<[string, string]> }
  | { links: Array<{ label: string; href: string }> };
export type Section = { id: string; title: string; blocks: Block[] };
export type LegalDoc = { title: string; intro: string; effective: string; sections: Section[] };
export type LegalSet = Record<Lang, LegalDoc>;

export type Inline = { text: string } | { placeholder: string } | { link: string; href: string };

/* splits a string into text, [placeholders] and {label|href} links */
export function inlineParts(s: string): Inline[] {
  const out: Inline[] = [];
  const re = /\[([^\]]+)\]|\{([^|}]+)\|([^}]+)\}/g;
  let last = 0;
  for (const m of s.matchAll(re)) {
    if (m.index! > last) out.push({ text: s.slice(last, m.index) });
    if (m[1] !== undefined) out.push({ placeholder: `[${m[1]}]` });
    else out.push({ link: m[2], href: m[3] });
    last = m.index! + m[0].length;
  }
  if (last < s.length) out.push({ text: s.slice(last) });
  return out;
}

/* every string in a section, for checks (placeholders and links must match across languages) */
export function sectionStrings(sec: Section): string[] {
  const out: string[] = [sec.title];
  for (const b of sec.blocks) {
    if ('p' in b) out.push(b.p);
    else if ('ul' in b) out.push(...b.ul);
    else if ('dl' in b) b.dl.forEach(([k, v]) => out.push(k, v));
    else out.push(...b.links.map(l => `${l.label} ${l.href}`));
  }
  return out;
}

/* features that use AI are labelled "AI" (EU AI Act, Art. 50): essay feedback is one */
/* an essay-feedback item that doesn't already say it's AI gets the AI tag (EU AI Act Art. 50) */
export const isAiFeature = (text: string) => /\bessay|مقال|निबंध/i.test(text) && !/\bAI\b|الذكاء الاصطناعي/.test(text);

/* ---------- a translated copy of the English document (Hindi) ----------
   Every string has an id from its place in the English document: "<doc>.title", "<doc>.intro", "<doc>.effective",
   "<doc>.<section id>.title", then per block j: ".<j>.p", ".<j>.<k>" (list item k), ".<j>.<k>.term" / ".<j>.<k>.desc"
   (definition k), ".<j>.<k>.label" (link k; the link target stays). Parts built from code (the plan list, the plan
   lengths) are passed in `computed`, keyed by the English block object or the English string, and aren't translated
   here. A string the table lacks stays in English. */
export type Computed = { blocks?: Map<Block, Block>; strings?: Map<string, string> };

export function docEntries(doc: LegalDoc, prefix: string, computed: Computed = {}): Array<[string, string]> {
  const out: Array<[string, string]> = [[`${prefix}.title`, doc.title], [`${prefix}.intro`, doc.intro], [`${prefix}.effective`, doc.effective]];
  const add = (id: string, s: string) => { if (!computed.strings?.has(s)) out.push([id, s]); };
  for (const sec of doc.sections) {
    const base = `${prefix}.${sec.id}`;
    out.push([`${base}.title`, sec.title]);
    sec.blocks.forEach((b, j) => {
      if (computed.blocks?.has(b)) return;
      if ('p' in b) add(`${base}.${j}.p`, b.p);
      else if ('ul' in b) b.ul.forEach((s, k) => add(`${base}.${j}.${k}`, s));
      else if ('dl' in b) b.dl.forEach(([t, d], k) => { add(`${base}.${j}.${k}.term`, t); add(`${base}.${j}.${k}.desc`, d); });
      else b.links.forEach((l, k) => add(`${base}.${j}.${k}.label`, l.label));
    });
  }
  return out;
}

export function localizeDoc(doc: LegalDoc, prefix: string, table: Readonly<Record<string, string>>, computed: Computed = {}): LegalDoc {
  const tr = (id: string, s: string) => computed.strings?.get(s) ?? table[id] ?? s;
  return {
    title: tr(`${prefix}.title`, doc.title),
    intro: tr(`${prefix}.intro`, doc.intro),
    effective: tr(`${prefix}.effective`, doc.effective),
    sections: doc.sections.map(sec => {
      const base = `${prefix}.${sec.id}`;
      return {
        id: sec.id,
        title: tr(`${base}.title`, sec.title),
        blocks: sec.blocks.map((b, j): Block => {
          const done = computed.blocks?.get(b);
          if (done) return done;
          if ('p' in b) return { p: tr(`${base}.${j}.p`, b.p) };
          if ('ul' in b) return { ul: b.ul.map((s, k) => tr(`${base}.${j}.${k}`, s)) };
          if ('dl' in b) return { dl: b.dl.map(([t, d], k): [string, string] => [tr(`${base}.${j}.${k}.term`, t), tr(`${base}.${j}.${k}.desc`, d)]) };
          return { links: b.links.map((l, k) => ({ label: tr(`${base}.${j}.${k}.label`, l.label), href: l.href })) };
        }),
      };
    }),
  };
}
